import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Truck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (!sid) { nav("/orders"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === "paid") {
          // Fetch buyer orders and find ETA for the newest paid order
          api.get("/orders?role=buyer").then((r) => {
            const paid = r.data.find((o) => o.stripe_session_id === sid || o.status === "paid");
            if (paid && paid.delivery_eta) setEta(paid.delivery_eta);
          });
          return;
        }
        if (attempts >= 10) { setState({ ...data, status: "timeout" }); return; }
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } catch (e) { console.warn("[GTF] payment poll", e); }
    };
    poll();
  }, [sid, attempts, nav]);

  const paid = state.payment_status === "paid";

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-16 max-w-xl mx-auto text-center">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring" }}
          className={`mx-auto h-24 w-24 rounded-3xl ${paid ? theme.bg : "bg-gray-200"} text-white flex items-center justify-center`}>
          <CheckCircle2 className="h-12 w-12" />
        </motion.div>
        <h1 className="mt-8 text-4xl font-bold text-gray-900 tracking-tight">
          {paid ? t("pay.success") : t("pay.verifying")}
        </h1>
        <p className="mt-3 text-gray-600">
          {paid ? t("pay.success_body") : t("pay.verifying_body")}
        </p>
        {paid && eta && (
          <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800">
            <Truck className="h-5 w-5" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest">{t("eta.title")}</div>
              <div className="font-semibold">{t("eta.by")} {new Date(eta).toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" })}</div>
            </div>
          </div>
        )}
        <button onClick={() => nav("/orders")} className={`mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full ${theme.bg} text-white font-semibold shadow-xl block mx-auto`}>
          {t("pay.view_orders")} <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </AppShell>
  );
}
